import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import { Link, useFetcher } from "@remix-run/react";
import { useEffect, useId, useRef, useState } from "react";

import CrossSVG from "~/svgs/Cross";
import type { SearchSuggestions } from "~/types";

type SearchSuggestionResponse = SearchSuggestions & { query: string };

const EMPTY_RESULTS: SearchSuggestions = {
  gremlins: [],
  users: [],
  streamers: [],
};

export default function SearchModal() {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<(HTMLAnchorElement | null)[]>([]);
  const {
    data: searchData,
    load: loadSearchResults,
    state: searchState,
  } = useFetcher<SearchSuggestionResponse>();

  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      setQuery("");
      setActiveIndex(null);
    },
    strategy: "absolute",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePressEvent: "mousedown",
  });
  const role = useRole(context);
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
  });
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: 100,
    initial: {
      opacity: 0,
      transform: "scale(0.9)",
    },
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    role,
    listNavigation,
  ]);

  const labelId = useId();
  const descriptionId = useId();
  const trimmedQuery = query.trim();
  const visibleResults = searchData?.query === trimmedQuery ? searchData : EMPTY_RESULTS;
  const searchResults = [
    ...visibleResults.gremlins.map((grem) => ({
      label: grem.name,
      type: "grem",
      to: `/marketplace/search?searchType=available&name=${encodeURIComponent(grem.name)}`,
    })),
    ...visibleResults.users.map((user) => ({
      label: user.display_name,
      type: "user",
      to: `/user/${encodeURIComponent(user.display_name)}/collection`,
    })),
    ...visibleResults.streamers.map((streamer) => ({
      label: streamer.display_name,
      type: "streamer",
      to: `/gremlins/${encodeURIComponent(streamer.display_name)}`,
    })),
  ];
  const isSearching =
    trimmedQuery.length >= 3 &&
    (searchState !== "idle" || searchData?.query !== trimmedQuery);

  useEffect(() => {
    if (trimmedQuery.length < 3) {
      return;
    }

    const delayId = setTimeout(() => {
      loadSearchResults(`/v2/search?q=${encodeURIComponent(trimmedQuery)}`);
    }, 300);

    return () => clearTimeout(delayId);
  }, [trimmedQuery, loadSearchResults]);

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        className="flex flex-col lg:flex-row items-center lg:items-end gap-x-0 lg:gap-x-2"
      >
        <svg
          width="24"
          height="24"
          xmlns="http://www.w3.org/2000/svg"
          fillRule="evenodd"
          clipRule="evenodd"
        >
          <path
            fill="#fefefe"
            d="M15.853 16.56c-1.683 1.517-3.911 2.44-6.353 2.44-5.243 0-9.5-4.257-9.5-9.5s4.257-9.5 9.5-9.5 9.5 4.257 9.5 9.5c0 2.442-.923 4.67-2.44 6.353l7.44 7.44-.707.707-7.44-7.44zm-6.353-15.56c4.691 0 8.5 3.809 8.5 8.5s-3.809 8.5-8.5 8.5-8.5-3.809-8.5-8.5 3.809-8.5 8.5-8.5z"
          />
        </svg>
        <p className="block lg:hidden">Search</p>
        <span className="hidden lg:block w-[32ch] border-b border-white/50 text-lg"></span>
      </button>
      {isMounted ? (
        <FloatingPortal>
          <FloatingOverlay
            lockScroll
            className="z-20 bg-black/80 h-screen transition-all"
          >
            <FloatingFocusManager context={context}>
              <div
                ref={refs.setFloating}
                aria-labelledby={labelId}
                aria-describedby={descriptionId}
                className="bg-card border border-white/15 absolute shadow rounded-md lg:min-w-[33vw] min-w-full top-1/2 lg:top-1/4 right-0 lg:right-1/3 -translate-y-1/2 p-6 text-xl"
                style={styles}
                {...getFloatingProps()}
              >
                <div className="flex w-full items-center gap-x-4">
                  <input
                    value={query}
                    onChange={(e) => {
                      setQuery(e.currentTarget.value);
                      setActiveIndex(null);
                    }}
                    placeholder="What are you looking for?"
                    className="bg-black border border-purple rounded-md px-2 py-1 placeholder:italic placeholder:font-extralight w-full focus-visible:outline focus-visible:outline-1 focus-visible:outline-purple"
                  />
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="cursor-pointer"
                    aria-label="Close search"
                  >
                    <CrossSVG />
                  </button>
                </div>
                <div className="w-full h-[1px] my-4 bg-white/20"></div>
                <div>
                  {trimmedQuery.length < 3 ? (
                    <p className="p-1 text-white/60">
                      Type at least 3 characters to search.
                    </p>
                  ) : isSearching ? (
                    <p className="p-1 text-white/60">Searching...</p>
                  ) : searchResults.length === 0 ? (
                    <p className="p-1 text-white/60">No matches found.</p>
                  ) : (
                    searchResults.map((result, index) => (
                      <Link
                        key={`${result.type}-${result.label}-${index}`}
                        className="p-1 flex justify-between bg-none hover:bg-white/10 focus:bg-white/10 transition-all items-center w-full font-main text-xl"
                        tabIndex={activeIndex === index ? 0 : -1}
                        ref={(node) => {
                          listRef.current[index] = node;
                        }}
                        {...getItemProps({ onClick: () => setIsOpen(false) })}
                        to={result.to}
                      >
                        <p className="text-2xl font-light">
                          <span className="font-semibold">{result.type}:</span>
                        </p>
                        <span className="font-extralight text-2xl">{result.label}</span>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      ) : null}
    </>
  );
}
